import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Calendar as CalIcon, X } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Portal } from '../../utils/Portal';
import { useFloatingPosition } from '../../utils/useFloatingPosition';
import { Calendar } from '../Calendar/Calendar';
import { useFormField } from '../FormField/FormField';

/**
 * DateRangePicker — sélecteur de période (du → au) avec presets.
 *
 *   <DateRangePicker
 *     value={{ from, to }}
 *     onChange={({from, to}) => ...}
 *     presets={[
 *       { label: '7 derniers jours', value: () => ({ from: subDays(7), to: today() }) },
 *       …
 *     ]}
 *   />
 *
 * Props :
 *   - value     : { from: Date|null, to: Date|null } | null
 *   - onChange  : (range) => void
 *   - presets   : array de { label, value: ({from,to}) | () => ({from,to}) }
 *   - twoMonths : afficher 2 mois côte à côte (default true)
 *   - minDate, maxDate
 *   - size, placeholder, isClearable, isDisabled, isInvalid
 */

const SIZES = {
  sm: 'h-8  text-sm   px-2.5',
  md: 'h-10 text-sm   px-3',
  lg: 'h-11 text-base px-3.5',
};

const RANGE_FMT = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

function startOfDay(d) { return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }

function defaultPresets() {
  return [
    {
      label: "Aujourd'hui",
      value: () => {
        const t = startOfDay(new Date());
        return { from: t, to: t };
      },
    },
    {
      label: 'Hier',
      value: () => {
        const t = startOfDay(new Date());
        t.setDate(t.getDate() - 1);
        return { from: t, to: t };
      },
    },
    {
      label: '7 derniers jours',
      value: () => {
        const t = startOfDay(new Date());
        const f = new Date(t);
        f.setDate(f.getDate() - 6);
        return { from: f, to: t };
      },
    },
    {
      label: '30 derniers jours',
      value: () => {
        const t = startOfDay(new Date());
        const f = new Date(t);
        f.setDate(f.getDate() - 29);
        return { from: f, to: t };
      },
    },
    {
      label: '90 derniers jours',
      value: () => {
        const t = startOfDay(new Date());
        const f = new Date(t);
        f.setDate(f.getDate() - 89);
        return { from: f, to: t };
      },
    },
    {
      label: 'Ce mois-ci',
      value: () => {
        const now = new Date();
        const f = new Date(now.getFullYear(), now.getMonth(), 1);
        const t = startOfDay(now);
        return { from: f, to: t };
      },
    },
    {
      label: 'Mois dernier',
      value: () => {
        const now = new Date();
        const f = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const t = new Date(now.getFullYear(), now.getMonth(), 0);
        return { from: f, to: t };
      },
    },
    {
      label: 'Cette année',
      value: () => {
        const now = new Date();
        const f = new Date(now.getFullYear(), 0, 1);
        const t = startOfDay(now);
        return { from: f, to: t };
      },
    },
  ];
}

export function DateRangePicker({
  value,
  onChange,
  presets,
  twoMonths = true,
  minDate,
  maxDate,
  size = 'md',
  placeholder = 'Choisir une période…',
  isClearable = true,
  isDisabled,
  isInvalid,
  className,
  id: idProp,
}) {
  const field = useFormField();
  const id = idProp || field?.id;
  const invalid = isInvalid || field?.invalid;

  const [open, setOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(() => value?.from || new Date());

  const triggerRef = useRef(null);
  const floatingRef = useRef(null);

  const { style } = useFloatingPosition({
    triggerRef,
    floatingRef,
    open,
    side: 'bottom',
    align: 'start',
    offset: 6,
  });

  useEffect(() => {
    if (!open) return;
    setVisibleMonth(value?.from || new Date());
    const onDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (floatingRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const display = useMemo(() => {
    if (!value || (!value.from && !value.to)) return '';
    const from = value.from ? RANGE_FMT.format(value.from) : '…';
    const to = value.to ? RANGE_FMT.format(value.to) : '…';
    return `${from} → ${to}`;
  }, [value]);

  const handleClear = (e) => {
    e.stopPropagation();
    onChange?.({ from: null, to: null });
  };

  const handleRange = (next) => {
    onChange?.(next);
    if (next.from && next.to) setOpen(false);
  };

  const handlePreset = (p) => {
    const r = typeof p.value === 'function' ? p.value() : p.value;
    onChange?.(r);
    setVisibleMonth(r.from);
    setOpen(false);
  };

  const usedPresets = presets || defaultPresets();

  // Second month for the side-by-side view
  const nextMonth = useMemo(() => {
    const d = new Date(visibleMonth);
    d.setMonth(d.getMonth() + 1);
    return d;
  }, [visibleMonth]);

  return (
    <div className={cn('relative inline-block w-full', className)}>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        onClick={() => !isDisabled && setOpen((o) => !o)}
        disabled={isDisabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-invalid={invalid || undefined}
        aria-describedby={field?.describedBy}
        className={cn(
          'w-full inline-flex items-center justify-between bg-surface text-text outline-none',
          'border border-border rounded-md transition-colors duration-fast',
          'focus:bg-surface focus:border-ink focus:ring-2 focus:ring-ink/15',
          'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-bg-2',
          SIZES[size],
          invalid && 'border-danger focus:border-danger focus:ring-danger/20',
        )}
      >
        <span className="inline-flex items-center gap-2 min-w-0">
          <CalIcon className="w-4 h-4 text-mute shrink-0" strokeWidth={1.8} />
          <span className={cn('truncate font-mono text-[12.5px]', display ? 'text-text' : 'text-mute-2')}>
            {display || placeholder}
          </span>
        </span>
        {isClearable && (value?.from || value?.to) && !isDisabled && (
          <span
            role="button"
            tabIndex={0}
            onClick={handleClear}
            className="text-mute hover:text-text p-0.5 ml-2"
            aria-label="Effacer"
          >
            <X className="w-3.5 h-3.5" strokeWidth={2} />
          </span>
        )}
      </button>

      {open && (
        <Portal>
          <div
            ref={floatingRef}
            data-ds-theme="default"
            role="dialog"
            style={style}
            className="z-overlay bg-surface border border-border rounded-lg shadow-md animate-scale-in origin-top-left p-3 flex gap-3"
          >
            {usedPresets.length > 0 && (
              <div className="w-40 shrink-0 border-r border-border pr-3 -my-1 py-1 overflow-y-auto max-h-[340px]">
                <div className="text-[10.5px] font-mono uppercase tracking-wider text-mute mb-1.5 px-1">
                  Périodes
                </div>
                <ul className="flex flex-col gap-0.5">
                  {usedPresets.map((p) => (
                    <li key={p.label}>
                      <button
                        type="button"
                        onClick={() => handlePreset(p)}
                        className="w-full text-left px-2 py-1.5 rounded text-[12.5px] text-text hover:bg-bg-2 transition-colors"
                      >
                        {p.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className={cn('flex gap-4', twoMonths ? 'flex-row' : 'flex-col')}>
              <div className="min-w-[260px]">
                <Calendar
                  mode="range"
                  selected={value}
                  onSelect={handleRange}
                  month={visibleMonth}
                  onMonthChange={setVisibleMonth}
                  minDate={minDate}
                  maxDate={maxDate}
                />
              </div>

              {twoMonths && (
                <div className="hidden md:block min-w-[260px]">
                  <Calendar
                    mode="range"
                    selected={value}
                    onSelect={handleRange}
                    month={nextMonth}
                    onMonthChange={(m) => {
                      const prev = new Date(m);
                      prev.setMonth(prev.getMonth() - 1);
                      setVisibleMonth(prev);
                    }}
                    minDate={minDate}
                    maxDate={maxDate}
                  />
                </div>
              )}
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
}

export default DateRangePicker;
